import { useState, useEffect } from "react";
import { MotifIcon, MotifProgressLoader } from "@ey-xd/motif-react";
import { IconoirWarningCircle } from "@ey-xd/motif-icon";
import orderEmptyImg from "../../../assets/images/empty_order.svg";
import OrdersTable from "../OrderTable/OrderTable";
import "../MyEstimates/MyEstimates.scss";
import { getDraftOrderColumns } from "../tableColumn";
import EmptyOrder from "../../EmptyOrder";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import productListEmptyImg from "../../../assets/images/empty_product_list_default.svg";
import ErrorOrderPage from "../../ErrorOrderPage/ErrorOrderPage";
import { getRetiredSkuListInWorkSpace } from "../helper";
import { RETIRED_SKU_IN_CHECKOUT_MESSAGE } from "../../../Constant";
import { containsEmoji } from "../../../utils/common";

interface MyDraftsProps {
  draftOrders: any[];
  isLoadingDrafts: boolean;
  getOrderAction?: (order: any) => React.ReactNode;
  searchText?: string;
  isDraftError: boolean;
  refreshCallback: () => void;
}

const MyDrafts: React.FC<MyDraftsProps> = ({
  draftOrders,
  isLoadingDrafts,
  getOrderAction,
  searchText,
  isDraftError,
  refreshCallback,
}) => {
  const [draftOrderDetails, setDraftOrderDetails] = useState<
    {
      orderParentId: string;
      isExpanded: boolean;
    }[]
  >([]);

  useEffect(() => {
    if (draftOrders.length > 0) {
      const newOrderDetails = draftOrders.map((order: any) => ({
        orderParentId: order.OrderParentId,
        isExpanded: false,
      }));
      setDraftOrderDetails(newOrderDetails);
    }
  }, [draftOrders]);

  if (isLoadingDrafts) {
    return <MotifProgressLoader show variant="default" />;
  }

  const draftOrderColumns = getDraftOrderColumns(
    setDraftOrderDetails,
    draftOrderDetails,
    getOrderAction
  );

  let content;

  const retiredSkuList = getRetiredSkuListInWorkSpace(draftOrders);
  const renderRetiredErrorMessage = () => {
    if (retiredSkuList.length === 0) {
      return;
    }
    return (
      <div
        className={`sku-retired-error-message ${
          retiredSkuList.length === 0 ? "hide" : ""
        }`}
      >
        <MotifIcon src={IconoirWarningCircle} className="warning-icon" />
        <div className="error-message">{RETIRED_SKU_IN_CHECKOUT_MESSAGE}</div>
      </div>
    );
  };

  if (!draftOrders.length) {
    if (isDraftError) {
      content = (
        <ErrorOrderPage
          image={productListEmptyImg}
          header="An error has occurred while attempting to retrieve data. Please choose the option to refresh the page."
          handleRefresh={refreshCallback}
        />
      );
    } else {
      const isContainEmoji = containsEmoji(searchText || "");
      if (searchText && !isContainEmoji) {
        content = <NoSearchResult searchValue={searchText} />;
      } else {
        content = (
          <EmptyOrder
            image={orderEmptyImg}
            header="No drafts created yet"
            subHeader="It looks like you do not have any drafts created yet. Please browse our technology products to start creating drafts."
          />
        );
      }
    }
  } else {
    content = (
      <OrdersTable
        data={draftOrders}
        orderDetails={draftOrderDetails}
        columnsData={draftOrderColumns}
        showEngagementColumn={true}
        retiredSkuList={retiredSkuList}
        showRetiredError={true}
      />
    );
  }

  return (
    <div className="workspace-orders-section">
      <div className="workspace-orders-list">
        <div className="workspace-orders-header">
          <div className="workspace-orders-header-grid">
            <div>
              <h3 className="workspace-orders-title">My Drafts</h3>
              <p className="workspace-orders-description">Drafts Created</p>
            </div>
            <div>{renderRetiredErrorMessage()}</div>
          </div>
        </div>
        <div className="workspace-orders-content">{content}</div>
      </div>
    </div>
  );
};

export default MyDrafts;
